import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMeta } from "@/hooks/useMeta";
import { rupiah, rupiahShort } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import NumberInput from "@/components/ui/number-input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { Trophy, Plus } from "lucide-react";
import CompanyForecast from "@/components/CompanyForecast";

export default function Targets() {
  const { user } = useAuth();
  const { salesUsers } = useMeta();
  const qc = useQueryClient();
  const month = new Date().toISOString().slice(0, 7);
  const [open, setOpen] = useState(false);
  const isAdmin = user?.role === "admin";
  const isOwnerOrChief = user?.role === "owner" || user?.role === "chief";
  const isAdminOrOwner = isAdmin || isOwnerOrChief;
  // Admin can now also set/edit SALES targets (unlike company-wide forecasting below,
  // which stays Owner/Chief-only, unchanged) — Admin runs day-to-day sales ops and
  // shouldn't have to go through Owner/Chief just to set a monthly target for someone.
  const canSetSalesTarget = isOwnerOrChief || isAdmin;
  const [mode, setMode] = useState(() => (isAdminOrOwner ? "perusahaan" : "sales")); // "sales" | "perusahaan" — perusahaan didahulukan untuk admin/owner/chief
  const [form, setForm] = useState({ sales_id: "", month, target_count: 5, target_value: 100000000, commission_pct: 2.5, produksi_commission_pct: 2.5 });

  const { data = [], isLoading } = useQuery({ queryKey: ["targets", month], queryFn: async () => (await api.get(`/targets?month=${month}`)).data, enabled: mode === "sales" });

  const save = async () => {
    if (!form.sales_id) { toast.error("Pilih sales"); return; }
    try {
      await api.post("/targets", { ...form, target_count: Number(form.target_count), target_value: Number(form.target_value), commission_pct: Number(form.commission_pct), produksi_commission_pct: Number(form.produksi_commission_pct) });
      toast.success("Target disimpan"); qc.invalidateQueries({ queryKey: ["targets"] }); setOpen(false);
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="px-4 py-5 max-w-3xl mx-auto md:max-w-none md:px-8">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-bold font-heading tracking-tight">Target & Komisi</h1>
          <p className="text-sm text-zinc-500">{mode === "sales" ? `Periode ${month}` : "Forecasting omset perusahaan"}</p>
        </div>
        {mode === "sales" && canSetSalesTarget && <Button data-testid="add-target-btn" onClick={() => setOpen(true)} className="bg-gradient-to-r from-blue-700 to-blue-900 h-9"><Plus size={16} className="mr-1" />Set Target</Button>}
      </div>

      {isAdminOrOwner && (
        <div className="flex bg-[#18181B] rounded-lg p-1 border border-white/5 mb-4 w-fit">
          <button data-testid="target-mode-perusahaan" onClick={() => setMode("perusahaan")} className={`px-3 py-1.5 rounded-md text-xs font-medium ${mode === "perusahaan" ? "bg-blue-500/20 text-blue-300" : "text-zinc-500"}`}>Perusahaan</button>
          <button data-testid="target-mode-sales" onClick={() => setMode("sales")} className={`px-3 py-1.5 rounded-md text-xs font-medium ${mode === "sales" ? "bg-blue-500/20 text-blue-300" : "text-zinc-500"}`}>Sales</button>
        </div>
      )}

      {mode === "perusahaan" ? (
        <CompanyForecast canEdit={isOwnerOrChief} />
      ) : isLoading ? null : (
        <div className="space-y-3">
          {!canSetSalesTarget && (
            <div className="rounded-xl bg-blue-500/10 border border-blue-500/20 px-3 py-2 text-xs text-blue-300">
              Mode lihat saja — hanya Admin/Owner/Chief yang bisa mengatur target &amp; komisi.
            </div>
          )}
          {data.map((t) => {
            const pct = Math.min(100, t.achievement_pct);
            return (
              <div key={t.target_id || t.sales_id} className="card-surface rounded-2xl p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-xl bg-blue-500/15 text-blue-300 flex items-center justify-center font-semibold">{t.sales_name?.[0]}</div>
                    <div>
                      <div className="font-semibold">{t.sales_name}</div>
                      <div className="text-xs text-zinc-500">{t.actual_count}/{t.target_count} closing · komisi Design {t.commission_pct}% · Produksi {t.produksi_commission_pct}%</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold font-heading" style={{ color: pct >= 100 ? "#10B981" : "#22D3EE" }}>{t.achievement_pct}%</div>
                    <div className="text-[11px] text-zinc-500">achievement</div>
                  </div>
                </div>
                <Progress value={pct} className="h-2 bg-[#18181B]" />
                <div className="flex justify-between mt-3 text-sm">
                  <div><span className="text-zinc-500 text-xs">Actual </span><span className="font-medium text-cyan-400">{rupiahShort(t.actual_value)}</span></div>
                  <div><span className="text-zinc-500 text-xs">Target </span><span className="font-medium">{rupiahShort(t.target_value)}</span></div>
                  <div className="flex items-center gap-1 text-emerald-400 font-semibold"><Trophy size={13} />{rupiah(t.commission_gabungan)}</div>
                </div>
                {t.actual_value_produksi > 0 && (
                  <div className="flex justify-between mt-1.5 text-xs text-zinc-500">
                    <span>Produksi: {rupiahShort(t.actual_value_produksi)}</span>
                    <span className="text-purple-400">Komisi Produksi: {rupiah(t.commission_produksi)}</span>
                  </div>
                )}
              </div>
            );
          })}
          {data.length === 0 && <div className="text-center text-zinc-600 py-16">Belum ada target bulan ini</div>}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0f0f12] border-white/10">
          <DialogHeader><DialogTitle className="font-heading">Set Target Sales</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-zinc-400 uppercase">Sales</Label>
              <Select value={form.sales_id || undefined} onValueChange={(v) => setForm({ ...form, sales_id: v })}>
                <SelectTrigger data-testid="target-sales" className="bg-[#18181B] border-white/10 mt-1"><SelectValue placeholder="Pilih sales" /></SelectTrigger>
                <SelectContent className="bg-[#18181B] border-white/10">{salesUsers.map((s) => <SelectItem key={s.user_id} value={s.user_id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-zinc-400 uppercase">Target Closing</Label><Input type="number" value={form.target_count} onChange={(e) => setForm({ ...form, target_count: e.target.value })} className="bg-[#18181B] border-white/10 mt-1" /></div>
              <div><Label className="text-xs text-zinc-400 uppercase">Target Nilai (IDR)</Label><NumberInput type="number" value={form.target_value} onChange={(e) => setForm({ ...form, target_value: e.target.value })} className="bg-[#18181B] border-white/10 mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-zinc-400 uppercase">Komisi Design %</Label><Input data-testid="target-commission" type="number" step="0.1" value={form.commission_pct} onChange={(e) => setForm({ ...form, commission_pct: e.target.value })} className="bg-[#18181B] border-white/10 mt-1" /></div>
              <div><Label className="text-xs text-zinc-400 uppercase">Komisi Produksi %</Label><Input data-testid="target-commission-produksi" type="number" step="0.1" value={form.produksi_commission_pct} onChange={(e) => setForm({ ...form, produksi_commission_pct: e.target.value })} className="bg-[#18181B] border-white/10 mt-1" /></div>
            </div>
            <Button data-testid="save-target" onClick={save} className="w-full bg-gradient-to-r from-blue-700 to-blue-900">Simpan Target</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
